'use client'

/**
 * `AnnouncementHost` — "What's New" announcements, app-wide (#14619).
 *
 * Mounted in the ROOT LAYOUT, so a banner persists across client navigation
 * until it is dismissed or its CTA followed, and a popup can appear on any
 * page. Two consequences of that position shape everything here:
 *
 *   - It is shell chrome, so it may not dispatch server actions
 *     (`src/lib/shell-transport.ts`). It reads ONCE per page load over
 *     `shellRead` — deferred until the browser is idle after first paint,
 *     so it never joins the mount-time burst — and records over `shellWrite`.
 *   - It costs a signed-out or impersonating viewer nothing: with no
 *     `viewerKey`, or while impersonating, it neither reads nor renders. The
 *     flag and the recipient check are the server's (`getAnnouncementsForShell`
 *     answers an empty list), so the layout does no extra work at render.
 *
 * Banner: the NEWEST live banner, one at a time; dismissing or following one
 * brings the next. Popup: the OLDEST unseen one, at most one per page load,
 * never over another open modal and never on the onboarding welcome; closing
 * it records it seen, so it does not come back.
 */

import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

import { useImpersonation } from '@/components/impersonation-context'
import type {
  AnnouncementActivity,
  AnnouncementSurface,
  DeliveredAnnouncement,
  ShellAnnouncementsPayload,
} from '@/lib/announcements/delivery'
import { SHELL_ENDPOINTS, shellRead, shellWrite } from '@/lib/shell-transport'
import { celebrateSmall } from '@/lib/training/celebration'
import { TRAINING_CONTEXT_PARAM } from '@/lib/training-utils'

import { AnnouncementBanner } from './announcement-banner'
import { AnnouncementModal } from './announcement-modal'

/** How often a deferred popup re-checks whether it may open. */
const MODAL_RETRY_MS = 500

/** Routes a popup never opens over — the onboarding welcome. */
const NO_POPUP_PREFIXES = ['/welcome']

const SEEN_STORAGE_KEY = 'cadence.announcements.seen'

/**
 * The viewer the module state below belongs to. Seen ids, the per-load
 * popup and the stored copy are all THAT person's: a persona switch, an org
 * switch or a different sign-in in the same tab must not inherit them, or
 * the next person's popup is suppressed and their `seen` never recorded.
 */
let sessionViewer: string | null = null

/**
 * `seenKey`s already recorded seen in this browser session, so `seen` is
 * sent once per announcement PER SURFACE per session even across reloads:
 * the event fires on every recorded call (#14953), so this is what keeps it
 * one per real sighting rather than one per page load.
 */
const seenThisSession = new Set<string>()

function seenKey(id: string, surface: AnnouncementSurface): string {
  return `${id}:${surface}`
}

/** At most one popup per page load (per viewer): module state is per load. */
let popupOpenedThisLoad = false

function storageKeyFor(viewer: string): string {
  return `${SEEN_STORAGE_KEY}:${viewer}`
}

/** Point the module state at `viewer`; a different viewer starts clean. */
function adoptViewer(viewer: string): void {
  if (sessionViewer === viewer) return
  sessionViewer = viewer
  seenThisSession.clear()
  popupOpenedThisLoad = false
  try {
    const raw = window.sessionStorage.getItem(storageKeyFor(viewer))
    const ids: unknown = raw ? JSON.parse(raw) : []
    if (Array.isArray(ids)) {
      for (const id of ids) if (typeof id === 'string') seenThisSession.add(id)
    }
  } catch {
    // Storage unavailable: the in-memory set suffices — see sub-issue R carve-out
  }
}

function writeSeenToStorage(): void {
  if (sessionViewer === null) return
  try {
    window.sessionStorage.setItem(
      storageKeyFor(sessionViewer),
      JSON.stringify([...seenThisSession]),
    )
  } catch {
    // Storage unavailable: nothing to persist — see sub-issue R carve-out
  }
}

/** Test seam: forget the session's viewer, seen set and per-load popup. */
export function resetAnnouncementHostForTests(): void {
  sessionViewer = null
  seenThisSession.clear()
  popupOpenedThisLoad = false
  try {
    for (const key of Object.keys(window.sessionStorage)) {
      if (key.startsWith(SEEN_STORAGE_KEY)) {
        window.sessionStorage.removeItem(key)
      }
    }
  } catch {
    // Storage unavailable: nothing to persist — see sub-issue R carve-out
  }
}

function anotherModalIsOpen(): boolean {
  return document.querySelector('[aria-modal="true"]') !== null
}

/** Run `fn` once the browser is idle after first paint. */
function afterFirstPaint(fn: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(fn, { timeout: 2000 })
    return () => window.cancelIdleCallback(handle)
  }
  const handle = window.setTimeout(fn, 0)
  return () => window.clearTimeout(handle)
}

/** Record `activity`; resolves `true` only when the server stored it. */
async function record(
  announcementId: string,
  activity: AnnouncementActivity,
  surface: AnnouncementSurface,
): Promise<boolean> {
  const result = await shellWrite<{ success?: boolean }>(
    SHELL_ENDPOINTS.announcements,
    { announcementId, activity, surface },
  )
  return result?.success === true
}

export function AnnouncementHost({ viewerKey }: { viewerKey: string | null }) {
  const { isImpersonating } = useImpersonation()
  const pathname = usePathname()
  // The training arrival popover owns the same top-center spot (#14953);
  // the banner waits while one is up.
  const trainingOpen = Boolean(useSearchParams()?.get(TRAINING_CONTEXT_PARAM))
  const [announcements, setAnnouncements] = useState<DeliveredAnnouncement[]>(
    [],
  )
  /** Banners dismissed or followed on this page load. */
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set())
  const [popup, setPopup] = useState<DeliveredAnnouncement | null>(null)
  /** The popup waiting for its turn; the retry timer runs only while set. */
  const [pendingPopup, setPendingPopup] =
    useState<DeliveredAnnouncement | null>(null)

  const active = viewerKey !== null && !isImpersonating

  useEffect(() => {
    // Every viewer change (org switch, persona switch, sign-in) starts from
    // nothing, so one person's banner, closed set or queued popup is never
    // shown to the next — not even while the new read is in flight or after
    // it fails.
    setAnnouncements([])
    setClosed(new Set())
    setPopup(null)
    setPendingPopup(null)
    if (!active || viewerKey === null) return
    let cancelled = false
    const cancelDefer = afterFirstPaint(() => {
      adoptViewer(viewerKey)
      void shellRead<ShellAnnouncementsPayload>(
        SHELL_ENDPOINTS.announcements,
      ).then(payload => {
        if (cancelled || !payload || !Array.isArray(payload.announcements)) {
          return
        }
        setAnnouncements(payload.announcements)
        // The list is newest first; the popup shown is the OLDEST unseen.
        const modals = payload.announcements.filter(
          a =>
            a.displayMode === 'modal' &&
            !seenThisSession.has(seenKey(a.id, 'modal')),
        )
        setPendingPopup(
          popupOpenedThisLoad ? null : (modals[modals.length - 1] ?? null),
        )
      })
    })
    return () => {
      cancelled = true
      cancelDefer()
    }
  }, [active, viewerKey])

  // Open the pending popup once nothing else is in the way. No pending
  // popup, no timer: this runs on every page for every signed-in viewer.
  useEffect(() => {
    if (!active || popup || !pendingPopup || popupOpenedThisLoad) return
    const blockedRoute = NO_POPUP_PREFIXES.some(prefix =>
      pathname?.startsWith(prefix),
    )
    if (blockedRoute) return
    const tryOpen = (): boolean => {
      if (popupOpenedThisLoad || anotherModalIsOpen()) return false
      popupOpenedThisLoad = true
      setPendingPopup(null)
      setPopup(pendingPopup)
      if (pendingPopup.celebrate) celebrateSmall()
      return true
    }
    if (tryOpen()) return
    const interval = window.setInterval(() => {
      if (tryOpen()) window.clearInterval(interval)
    }, MODAL_RETRY_MS)
    return () => window.clearInterval(interval)
  }, [active, popup, pendingPopup, pathname])

  const markSeen = useCallback((id: string, surface: AnnouncementSurface) => {
    const key = seenKey(id, surface)
    if (seenThisSession.has(key)) return
    seenThisSession.add(key)
    writeSeenToStorage()
    // Remembered optimistically so a second render does not send it
    // again; forgotten if the server did not store it, so the next mount
    // in this session retries rather than never recording it.
    void record(id, 'seen', surface).then(stored => {
      if (stored) return
      seenThisSession.delete(key)
      writeSeenToStorage()
    })
  }, [])

  const closeBanner = useCallback((id: string) => {
    setClosed(previous => new Set(previous).add(id))
  }, [])

  if (!active) return null

  // One announcement at a time: the banner waits while the popup is open.
  const banner =
    trainingOpen || popup
      ? undefined
      : announcements.find(a => a.displayMode === 'banner' && !closed.has(a.id))

  return (
    <>
      {banner && (
        <AnnouncementBanner
          key={banner.id}
          announcement={banner}
          alreadySeen={seenThisSession.has(seenKey(banner.id, 'banner'))}
          onSeen={() => markSeen(banner.id, 'banner')}
          onExpand={() => void record(banner.id, 'expanded', 'banner')}
          onDismiss={() => {
            void record(banner.id, 'dismissed', 'banner')
            closeBanner(banner.id)
          }}
          onCta={() => {
            void record(banner.id, 'cta_clicked', 'banner')
            closeBanner(banner.id)
          }}
        />
      )}
      {popup && (
        <AnnouncementModal
          key={popup.id}
          announcement={popup}
          onSeen={() => markSeen(popup.id, 'modal')}
          onLater={() => {
            markSeen(popup.id, 'modal')
            void record(popup.id, 'later', 'modal')
            setPopup(null)
          }}
          onClose={() => {
            markSeen(popup.id, 'modal')
            void record(popup.id, 'dismissed', 'modal')
            setPopup(null)
          }}
          onCta={() => {
            markSeen(popup.id, 'modal')
            void record(popup.id, 'cta_clicked', 'modal')
            setPopup(null)
          }}
        />
      )}
    </>
  )
}
